'use client';

import { useTranslations } from 'next-intl';
import {
  Eye,
  Pencil,
  Trash2,
  Package,
  MapPin,
  CalendarDays,
  Weight,
  Truck
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Link } from '@/i18n/routing';
import { PermissionGate } from '@/features/auth';
import { RouteGate } from '@/components/route-gate';
import { ShippingPriorityTag } from './shipping-priority-tag';
import type { Shipment, ShipmentStatus } from '../types';

interface ShippingCardProps {
  shipment: Shipment;
  onEdit: (shipment: Shipment) => void;
  onDelete: (shipment: Shipment) => void;
}

const statusStyles: Record<ShipmentStatus, string> = {
  draft:
    'border-orange-300 bg-orange-50 text-orange-800 dark:border-orange-700 dark:bg-orange-900/30 dark:text-orange-200',
  ready_for_shipping:
    'border-green-300 bg-green-50 text-green-800 dark:border-green-700 dark:bg-green-900/30 dark:text-green-200',
  in_transit:
    'border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-200',
  received:
    'border-teal-300 bg-teal-50 text-teal-800 dark:border-teal-700 dark:bg-teal-900/30 dark:text-teal-200'
};

const statusDotColors: Record<ShipmentStatus, string> = {
  draft: 'bg-orange-500',
  ready_for_shipping: 'bg-green-500',
  in_transit: 'bg-blue-500',
  received: 'bg-teal-500'
};

const statusLabels: Record<ShipmentStatus, string> = {
  draft: 'statusDraft',
  ready_for_shipping: 'statusReadyForShipping',
  in_transit: 'statusInTransit',
  received: 'statusReceived'
};

export function ShippingCard({
  shipment,
  onEdit,
  onDelete
}: ShippingCardProps) {
  const t = useTranslations('shipping');

  return (
    <Card className='border-border hover:border-primary/30 transition-colors'>
      <CardContent className='p-6'>
        {/* Top: shipment number + status + priority */}
        <div className='mb-4 flex items-start justify-between'>
          <div className='flex flex-col gap-1'>
            <span className='font-mono text-sm font-bold'>
              #{shipment.shipment_number}
            </span>
            <div className='flex items-center gap-2'>
              <div
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium',
                  statusStyles[shipment.status]
                )}
              >
                <span
                  className={cn(
                    'inline-block h-1.5 w-1.5 shrink-0 rounded-full',
                    statusDotColors[shipment.status]
                  )}
                />
                {t(statusLabels[shipment.status])}
              </div>
              <ShippingPriorityTag priority={shipment.priority} />
            </div>
          </div>
        </div>

        {/* Middle: details */}
        <div className='text-muted-foreground mb-4 space-y-1.5 text-sm'>
          <p className='text-foreground font-medium'>
            {shipment.judicial_entity}
          </p>
          <div className='flex items-center gap-1.5'>
            <Package className='h-3.5 w-3.5 shrink-0' />
            <span>{t('boxCountLabel', { count: shipment.box_count })}</span>
          </div>
          {shipment.driver?.name && (
            <div className='flex items-center gap-1.5'>
              <Truck className='h-3.5 w-3.5 shrink-0' />
              <span>{shipment.driver.name}</span>
            </div>
          )}
          {shipment.shipment_date && (
            <div className='flex items-center gap-1.5'>
              <CalendarDays className='h-3.5 w-3.5 shrink-0' />
              <span>{shipment.shipment_date}</span>
            </div>
          )}
          {shipment.total_weight_kg != null && (
            <div className='flex items-center gap-1.5'>
              <Weight className='h-3.5 w-3.5 shrink-0' />
              <span>{shipment.total_weight_kg} kg</span>
            </div>
          )}
          {shipment.location && (
            <div className='flex items-center gap-1.5'>
              <MapPin className='h-3.5 w-3.5 shrink-0' />
              <span>{shipment.location}</span>
            </div>
          )}
        </div>

        {/* Bottom: action buttons */}
        <div className='flex items-center justify-end gap-1 border-t pt-3'>
          <RouteGate route={`/dashboard/shipping/${shipment.id}`}>
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400'
              asChild
            >
              <Link href={`/dashboard/shipping/${shipment.id}`}>
                <Eye className='h-4 w-4' />
              </Link>
            </Button>
          </RouteGate>
          {shipment.status !== 'received' && (
            <PermissionGate permissions={['shipments.edit']}>
              <Button
                variant='ghost'
                size='icon'
                className='h-8 w-8 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400'
                onClick={() => onEdit(shipment)}
              >
                <Pencil className='h-4 w-4' />
              </Button>
            </PermissionGate>
          )}
          {shipment.status !== 'received' && (
            <PermissionGate permissions={['shipments.delete']}>
              <Button
                variant='ghost'
                size='icon'
                className='h-8 w-8 rounded-full bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400'
                onClick={() => onDelete(shipment)}
              >
                <Trash2 className='h-4 w-4' />
              </Button>
            </PermissionGate>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
